import type { ColumnDef } from '@tanstack/react-table';
import type { SxProps, Theme } from '@mui/material/styles';

import { Box } from 'ui/box';
import { mergeSx } from 'ui/merge-sx';

import ChangeBadge from './change-badge';

import type { OverviewRow } from '../results-step.types';

const VALUE_CELL_SX: SxProps<Theme> = {
  fontSize: '1rem',
  lineHeight: '1.5rem',
  color: 'tokens.gray.700',
};

const LABEL_CELL_SX: SxProps<Theme> = mergeSx(VALUE_CELL_SX, {
  fontWeight: 600,
});

const commonHeaderStyles =
  'font-semibold uppercase cursor-default text-xs py-3';

interface OverviewTableSettingsParams {
  showModeledActualYear: boolean;
}

export const getOverviewTableSettings = ({
  showModeledActualYear,
}: OverviewTableSettingsParams): ColumnDef<OverviewRow>[] => {
  if (showModeledActualYear) {
    return [
      {
        id: 'metric',
        header: () => <span>Metric</span>,
        cell: ({ row }) => (
          <Box component="span" sx={LABEL_CELL_SX}>
            {row.original.metric}
          </Box>
        ),
        headerClassName: `${commonHeaderStyles} rounded-tl-md rounded-bl-md`,
        className: 'w-[18%] px-2.5 py-2',
        enableSorting: false,
      },
      {
        id: 'actualYear',
        header: () => <span>Current Year</span>,
        cell: ({ row }) => (
          <Box component="span" sx={VALUE_CELL_SX}>
            {row.original.actualYear}
          </Box>
        ),
        headerClassName: commonHeaderStyles,
        className: 'w-[14%] px-2.5 py-2',
        enableSorting: false,
      },
      {
        id: 'modeledActualYear',
        header: () => <span>Modeled Current Year</span>,
        cell: ({ row }) => (
          <Box component="span" sx={VALUE_CELL_SX}>
            {row.original.modeledActualYear}
          </Box>
        ),
        headerClassName: commonHeaderStyles,
        className: 'w-[20%] px-2.5 py-2',
        enableSorting: false,
      },
      {
        id: 'modeledThisYear',
        header: () => <span>Modeled Future Year</span>,
        cell: ({ row }) => (
          <Box component="span" sx={VALUE_CELL_SX}>
            {row.original.modeledThisYear}
          </Box>
        ),
        headerClassName: commonHeaderStyles,
        className: 'w-[20%] px-2.5 py-2',
        enableSorting: false,
      },
      {
        id: 'percentChange',
        header: () => <span>% change</span>,
        cell: ({ row }) => (
          <ChangeBadge
            change={row.original.percentChange}
            data-testid={`results-percent-change-${row.original.key}`}
          />
        ),
        headerClassName: commonHeaderStyles,
        className: 'w-[14%] px-2.5 py-2',
        enableSorting: false,
      },
      {
        id: 'volumeChange',
        header: () => <span>Vol change</span>,
        cell: ({ row }) => (
          <ChangeBadge
            change={row.original.volumeChange}
            data-testid={`results-volume-change-${row.original.key}`}
          />
        ),
        headerClassName: `${commonHeaderStyles} rounded-tr-md rounded-br-md`,
        className: 'w-[14%] px-2.5 py-2',
        enableSorting: false,
      },
    ];
  }

  return [
    {
      id: 'metric',
      header: () => <span>Metric</span>,
      cell: ({ row }) => (
        <Box component="span" sx={LABEL_CELL_SX}>
          {row.original.metric}
        </Box>
      ),
      headerClassName: `${commonHeaderStyles} rounded-tl-md rounded-bl-md`,
      className: 'w-[28%] px-2.5 py-2',
      enableSorting: false,
    },
    {
      id: 'actualYear',
      header: () => <span>Current Year</span>,
      cell: ({ row }) => (
        <Box component="span" sx={VALUE_CELL_SX}>
          {row.original.actualYear}
        </Box>
      ),
      headerClassName: commonHeaderStyles,
      className: 'w-[18%] px-2.5 py-2',
      enableSorting: false,
    },
    {
      id: 'modeledThisYear',
      header: () => <span>Modeled Future Year</span>,
      cell: ({ row }) => (
        <Box component="span" sx={VALUE_CELL_SX}>
          {row.original.modeledThisYear}
        </Box>
      ),
      headerClassName: commonHeaderStyles,
      className: 'w-[22%] px-2.5 py-2',
      enableSorting: false,
    },
    {
      id: 'percentChange',
      header: () => <span>% change</span>,
      cell: ({ row }) => (
        <ChangeBadge
          change={row.original.percentChange}
          data-testid={`results-percent-change-${row.original.key}`}
        />
      ),
      headerClassName: commonHeaderStyles,
      className: 'w-[16%] px-2.5 py-2',
      enableSorting: false,
    },
    {
      id: 'volumeChange',
      header: () => <span>Vol change</span>,
      cell: ({ row }) => (
        <ChangeBadge
          change={row.original.volumeChange}
          data-testid={`results-volume-change-${row.original.key}`}
        />
      ),
      headerClassName: `${commonHeaderStyles} rounded-tr-md rounded-br-md`,
      className: 'w-[16%] px-2.5 py-2',
      enableSorting: false,
    },
  ];
};
